import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaFileAlt, FaTools, FaLightbulb,
  FaTint, FaBolt, FaIdCard, FaBug, FaRoad, FaTrash, FaWater,
  FaMapMarkerAlt, FaCalendarAlt, FaChevronRight, FaExclamationTriangle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import './Dashboard.css';

const TAB_META = {
  complaints: { label: 'Complaints', singular: 'Complaint', icon: FaFileAlt },
  requests: { label: 'Service Requests', singular: 'Service Request', icon: FaTools },
  suggestions: { label: 'Suggestions', singular: 'Suggestion', icon: FaLightbulb },
};

const STATUS_LABEL = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
};

// Rotating card palette: [border/icon color, icon tint background]
const CARD_PALETTE = [
  { accent: '#e14848', soft: '#fde3e2' },
  { accent: '#f2891a', soft: '#fdead1' },
  { accent: '#3b6fe0', soft: '#e3ebfd' },
  { accent: '#2fa860', soft: '#e0f5e8' },
  { accent: '#8b5cf6', soft: '#ece5fb' },
];

// Pick a relevant icon by matching keywords in the title, so cards
// read at a glance instead of relying on one generic tab icon.
const getItemIcon = (title = '', fallback) => {
  const t = title.toLowerCase();
  if (/pipe|leak|water/.test(t)) return FaTint;
  if (/transformer|power|electric|light/.test(t)) return FaBolt;
  if (/voter|id\b/.test(t)) return FaIdCard;
  if (/dengue|malaria|mosquito|pest/.test(t)) return FaBug;
  if (/road|pothole/.test(t)) return FaRoad;
  if (/garbage|waste|trash/.test(t)) return FaTrash;
  if (/drain|sewage/.test(t)) return FaWater;
  return fallback;
};

const Dashboard = () => {

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("complaints");
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {

    setLoading(true);

    try {

      let endpoint = "";

      if (activeTab === "complaints") {
        endpoint = "/api/corporator/complaints";
      }

      if (activeTab === "requests") {
        endpoint = "/api/corporator/requests";
      }

      if (activeTab === "suggestions") {
        endpoint = "/api/corporator/suggestions";
      }

      const res = await api.get(endpoint, {
        params: {
          status: status || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });

      console.log("API RESPONSE:", res.data);

      let data = [];

      if (Array.isArray(res.data)) {
        data = res.data;
      }
      else if (Array.isArray(res.data?.data)) {
        data = res.data.data;
      }
      else if (
        res.data?.success &&
        Array.isArray(res.data?.data)
      ) {
        data = res.data.data;
      }

      setItems(data);

    } catch (error) {

      console.error(error);
      toast.error("Failed to load data");
      setItems([]);

    } finally {

      setLoading(false);

    }

  }, [
    activeTab,
    status,
    fromDate,
    toDate
  ]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const countByStatus = (status) =>
    items.filter(
      item =>
        item?.status?.toUpperCase() === status
    ).length;

  const countByPriority = (priority) =>
    items.filter(
      item =>
        item?.priority?.toUpperCase() === priority
    ).length;

  const getStatusClass = (status) => {

    if (status === "OPEN") return "badge-open";
    if (status === "IN_PROGRESS") return "badge-progress";
    if (status === "RESOLVED") return "badge-resolved";

    return "";
  };

  const formatDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatLocation = (item) => {
    const loc = item?.ward || item?.location || item?.address;

    if (!loc) return 'Ward office record';
    if (typeof loc === 'string') return loc;

    // Handle nested shape: { address, ward, coordinates }
    return loc.address || loc.ward || 'Ward office record';
  };

  const priorityPillClass = (priority) => {
    if (priority === 'HIGH') return 'priority-high';
    if (priority === 'MEDIUM') return 'priority-medium';
    return 'priority-low';
  };

  if (loading) {
    return (
      <div className="registry-loading">
        <div className="registry-loading-mark" />
        <span>Pulling records&hellip;</span>
      </div>
    );
  }

  const activeMeta = TAB_META[activeTab];

  return (
    <div className="registry">

      {/* ===== Header ===== */}
      <header className="registry-header">
        <div className="registry-header-text">
          <p className="registry-eyebrow">Ward Office &middot; Public Records</p>
          <h1>Constituent Case Register</h1>
        </div>
        <div className="registry-header-count">
          <span className="registry-header-count-num">{items.length}</span>
          <span className="registry-header-count-label">on file</span>
        </div>
      </header>

      {/* ===== Folder Tabs ===== */}
      <div className="folder-tabs">
        {Object.entries(TAB_META).map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <button
              key={key}
              className={activeTab === key ? "folder-tab active" : "folder-tab"}
              onClick={() => setActiveTab(key)}
            >
              <Icon aria-hidden="true" />
              {meta.label}
            </button>
          );
        })}
      </div>

      <div className="registry-sheet">

        {/* ===== Filters ===== */}
        <div className="filter-row">

          <label className="filter-field">
            <span>Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="OPEN">Open</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="RESOLVED">Resolved</option>
            </select>
          </label>

          <label className="filter-field">
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>

          <label className="filter-field">
            <span>To</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>

          <div className="filter-actions">
            <button className="btn-primary" onClick={fetchItems}>
              Apply filters
            </button>
            <button
              className="btn-ghost"
              onClick={() => {
                setStatus('');
                setFromDate('');
                setToDate('');
              }}
            >
              Reset
            </button>
          </div>

        </div>

        {/* ===== Summary Ledger ===== */}
        <div className="ledger-strip">

          <div className="ledger-stat">
            <span className="ledger-stat-num">{items.length}</span>
            <span className="ledger-stat-label">Total</span>
          </div>

          <div className="ledger-stat is-open">
            <span className="ledger-stat-num">{countByStatus("OPEN")}</span>
            <span className="ledger-stat-label">Open</span>
          </div>

          <div className="ledger-stat is-progress">
            <span className="ledger-stat-num">{countByStatus("IN_PROGRESS")}</span>
            <span className="ledger-stat-label">In Progress</span>
          </div>

          <div className="ledger-stat is-resolved">
            <span className="ledger-stat-num">{countByStatus("RESOLVED")}</span>
            <span className="ledger-stat-label">Resolved</span>
          </div>

          {activeTab === "complaints" && (
            <div className="ledger-stat is-high">
              <span className="ledger-stat-num">{countByPriority("HIGH")}</span>
              <span className="ledger-stat-label">High Priority</span>
            </div>
          )}

        </div>

        {/* ===== Case Cards ===== */}
        <div className="case-list">

          {items.length === 0 ? (

            <div className="empty-state">
              <p className="empty-state-title">No {activeMeta.label.toLowerCase()} on file</p>
              <p className="empty-state-sub">Records will appear here once submitted for your ward.</p>
            </div>

          ) : (

            [...items]
              .sort((a, b) => {

                const priorityOrder = {
                  HIGH: 3,
                  MEDIUM: 2,
                  LOW: 1
                };

                return (
                  (priorityOrder[b?.priority?.toUpperCase()] || 0)
                  -
                  (priorityOrder[a?.priority?.toUpperCase()] || 0)
                );

              })
              .map((item, index) => {

                const Icon = getItemIcon(item?.title, activeMeta.icon);
                const priority = item?.priority?.toUpperCase() || 'LOW';
                const palette = CARD_PALETTE[index % CARD_PALETTE.length];
                const date = formatDate(item?.createdAt || item?.date || item?.submittedAt);
                const location = formatLocation(item);

                return (
                  <div
                    key={item.id}
                    className="case-row"
                    style={{ '--row-accent': palette.accent, '--row-accent-soft': palette.soft }}
                   onClick={() => {

    if (activeTab === "complaints") {
        navigate(`/detail/complaint/${item.id}`);
    }

    else if (activeTab === "requests") {
        navigate(`/detail/request/${item.id}`);
    }

    else if (activeTab === "suggestions") {
        navigate(`/detail/suggestion/${item.id}`);
    }

}}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {

  if (e.key !== "Enter") return;

  if (activeTab === "complaints") {
    navigate("/item/" + item.id);
  }

  else if (activeTab === "requests") {
    navigate("/service-request/" + item.id);
  }

}}
                  >

                    <span className="case-row-icon">
                      <Icon aria-hidden="true" />
                    </span>

                    <div className="case-row-main">

                      <div className="case-row-head">
                        <h3 className="case-row-title">{item.title}</h3>

                        {activeTab === "complaints" && (
                          <span className={`priority-pill ${priorityPillClass(priority)}`}>
                            {priority === 'HIGH' && <FaExclamationTriangle aria-hidden="true" />}
                            {priority}
                          </span>
                        )}
                      </div>

                      <div className="case-row-meta">
                        <span><FaMapMarkerAlt aria-hidden="true" />{location}</span>
                        {date && <span><FaCalendarAlt aria-hidden="true" />{date}</span>}
                      </div>

                      <div className="case-row-badges">
                        {activeTab === "complaints" && (
                          <span className={`priority-pill ${priorityPillClass(priority)}`}>
                            {priority === 'HIGH' && <FaExclamationTriangle aria-hidden="true" />}
                            {priority}
                          </span>
                        )}
                        <span className={`status-pill ${getStatusClass(item?.status)}`}>
                          {STATUS_LABEL[item?.status] || item?.status}
                        </span>
                      </div>

                      {item?.description && (
                        <p className="case-row-desc">{item.description}</p>
                      )}

                    </div>

                    <FaChevronRight className="case-row-chevron" aria-hidden="true" />

                  </div>
                );
              })

          )}

        </div>

      </div>

    </div>
  );
};

export default Dashboard;